import { autoinject } from "aurelia-dependency-injection";
import { useView } from "aurelia-framework";
import * as Enumerable from 'linq';
import { MepChartColors } from "../../../../../../../../constants/mep/mep.constants";
import {
    BaseMepSummaryRequest, MepEmissionsByPort, MepGetEmissionsByPortsRequest, MepGetEmissionsByPortsResponse, MepGetSummaryVesselStatisticsRequest, MepGetSummaryVesselStatisticsResponse, MepPortSummaryTotalEmissionsResponse,
    MepPortSummaryVesselsByPortRequest, MepPortSummaryVesselsByPortResponse,
    SummaryVesselStatisticsNames, SummaryVesselStatisticsUIModel, VesselByPortModel
} from "../../../../../../../../models/mep/mep-summary.model";
import { EmissionTypeCategoryEnum, MepPortMenuItem } from "../../../../../../../../models/mep/mep.models";
import { NotificationClientService, NotificationType } from "../../../../../../../../services/basics/notification-client.service";
import { MepLookupService } from "../../../../../../../../services/mep/mep-lookup.service";
import { MepSummaryService } from "../../../../../../../../services/mep/mep-summary.service";
import { MepSingleEmissionType } from "../../../mep-dashboard/partials/mep-single-emission-type/mep-single-emission-type";
import { MepPortsSummaryFilter } from "../../partials/mep-ports-summary-filter/mep-ports-summary-filter";

@autoinject()
@useView('views/pages/modules/mep/tabs/mep-home/tabs/mep-ports-summary/mep-ports-summary.html')
export class MepPortsSummary {

    public customerId: number;
    public filterViewModel: MepPortsSummaryFilter;
    public isVesselCalls: boolean = true;
    public isGhgEmissions: boolean = true;
    public vesselsByPortGraph: any;
    public emissionsByPortGraph: any;
    public ports: MepPortMenuItem[];
    public portColors: { portId: number, color: string }[] = [];

    public vesselStatisticsItems: SummaryVesselStatisticsUIModel[];
    public totalEmissions: MepPortSummaryTotalEmissionsResponse;
    public singleEmissionTypeViewModel: MepSingleEmissionType;
    public vesselsByPort: VesselByPortModel[];
    public emissionsByPort: MepEmissionsByPort[];

    //Loaders
    public isVesselStatisticsLoading: boolean;
    public isTotalEmissionsLoading: boolean;
    public isVesselsByPortLoading: boolean;
    public isEmissionsByPortLoading: boolean;
    
    refreshDataFunc: (request) => Promise<string[]>;

    constructor(private mepLookupService: MepLookupService, private mepSummaryService: MepSummaryService,
        private notificationClientService: NotificationClientService) {
    
        this.refreshDataFunc = (request) => this.reloadData(request);
    }

    activate(params: { customerId: number, ports: MepPortMenuItem[] }) {
        
        if (!params)
            return;

        this.customerId = params.customerId;
        this.ports = params.ports;

        const portsId = this.ports.map(x => x.portId);
         
        //Assign colors to the ports based on the port ids
        this.assignColorToPort(portsId);

        if (!this.filterViewModel)
            this.initializeFilterVM();
    }

    attached() {
        this.setInitLookups(this.customerId);
    }

    //Initialize the filter view model
    initializeFilterVM() {
        this.filterViewModel = new MepPortsSummaryFilter(this.refreshDataFunc, this.mepLookupService, this.customerId, this.notificationClientService);
        this.singleEmissionTypeViewModel = new MepSingleEmissionType(this.mepSummaryService, this.filterViewModel);
        return this.filterViewModel;
    }

    setInitLookups(customerId: number) {
        this.filterViewModel.initLookups(customerId).then(() => {

            this.filterViewModel.applyAllFilters(true);
        }).catch(() => {
            this.notificationClientService.show("MEP Error", "Error loading year!", NotificationType.Error);
        });
    }

    refresh(data?: { customerId: number }) {

        if (data && data.customerId){
            this.customerId = data.customerId;
        }
        this.filterViewModel = null;
        this.initializeFilterVM();
        this.setInitLookups(this.customerId);
    }

    reloadData(request): Promise<string[]> {
        this.clearData();

        const promises = [];
        promises.push(this.getVesselStatisticsData(request));
        const totalEmissionsPromise = this.getTotalEmissionsData(request);
        promises.push(totalEmissionsPromise);
        const p = this.singleEmissionTypeViewModel.refreshPortSummaryData(totalEmissionsPromise, request);
        if (p) promises.push(p);
        promises.push(this.getVesselsByPortData(request));
        promises.push(this.getEmissionsByPortData(request));

        return Promise.all(promises);
    }

    //Get vessel statistics data
    getVesselStatisticsData(request: BaseMepSummaryRequest) {
        return new Promise<MepGetSummaryVesselStatisticsResponse>((resolve, reject) => {
            this.isVesselStatisticsLoading = true;

            //Set the vessel statistics request based on the filter view model
            const vesselStatisticsRequest: MepGetSummaryVesselStatisticsRequest = {
                year: request.year,
                lastYear: request.year - 1,
                portIds: request.portIds,
                vesselTypeGroupIds: request.vesselTypeGroupIds,
                operationModeIds: request.operationModeIds,
                emissionTypeIds: request.emissionTypeIds,
                scopeIds: request.scopeIds
            };

            //Api call to get vessel statistics
            this.mepSummaryService.getMepVesselStatisticsByCustomer(vesselStatisticsRequest).then(response => {
                if (response.summaryVesselStatistics) {
                    this.vesselStatisticsItems = [
                        new SummaryVesselStatisticsUIModel(SummaryVesselStatisticsNames.VesselCalls,
                            response.summaryVesselStatistics.vesselCallsAndTugboatJobs.vesselCalls,
                            response.vesselCallsPercentage),
                        new SummaryVesselStatisticsUIModel(SummaryVesselStatisticsNames.VesselCount,
                            response.summaryVesselStatistics.vesselCount.vesselCount,
                            response.vesselCountPercentage),
                        new SummaryVesselStatisticsUIModel(SummaryVesselStatisticsNames.TugboatJobs,
                            response.summaryVesselStatistics.vesselCallsAndTugboatJobs.tugboatJobs,
                            response.tugboatJobsPercentage)
                    ]
                }

                this.isVesselStatisticsLoading = false;
                resolve(null);
            }).catch(() => {
                this.isVesselStatisticsLoading = false;
                reject("MEP Vessel Statistics - Error retrieving the vessel statistics!");
            });
        });
    }

    //Get total emissions data
    getTotalEmissionsData(request: BaseMepSummaryRequest) {
        return new Promise<MepPortSummaryTotalEmissionsResponse>((resolve, reject) => {
            this.isTotalEmissionsLoading = true;
            this.mepSummaryService.getMepPortSummaryTotalEmissions(request).then(response => {
                this.isTotalEmissionsLoading = false;
                if (!response.hasError) {
                    this.totalEmissions = response;
                    resolve(response);
                } else {
                    reject("MEP Dashboard Summary - Error retrieving the total emissions!");
                }

            }).catch(() => {
                this.isTotalEmissionsLoading = false;
                reject("MEP Dashboard Summary - Error retrieving the total emissions!");
            });
        });
    }

    //Get Vessels by port data
    getVesselsByPortData(request: MepPortSummaryVesselsByPortRequest) {
        return new Promise<MepPortSummaryVesselsByPortResponse>((resolve, reject) => {
            this.isVesselsByPortLoading = true;
            this.mepSummaryService.getMepPortSummaryVesselsByPort(request).then(response => {
                this.isVesselsByPortLoading = false;
                if (!response.hasError) {
                    this.vesselsByPort = response.vesselByPorts;
                    this.vesselsByPortGraph = this.generateVesselsByPortGraph(this.vesselsByPort, this.isVesselCalls ? "vesselCalls" : "tugJobs", 5, "portName");
                    resolve(response);
                } else {
                    reject("MEP Dashboard Summary - Error retrieving vessels by port data!");
                }

            }).catch(() => {
                this.isVesselsByPortLoading = false;
                reject("MEP Dashboard Summary - Error retrieving vessels by port data!");
            });
        });
    }

    //Get Emissions by port data
    getEmissionsByPortData(request: MepGetEmissionsByPortsRequest) {
        return new Promise<MepGetEmissionsByPortsResponse>((resolve, reject) => {
            this.isEmissionsByPortLoading = true;
            this.mepSummaryService.getMepEmissionsByPorts(request).then(response => {
                this.isEmissionsByPortLoading = false;
                if (!response.hasError) {
                    this.emissionsByPort = response.emissionsByPort;
                    this.emissionsByPortGraph = this.generateEmissionsByPortGraph(this.emissionsByPort, 'totalWeightedEmissionsinTonnes', 5, 'portName');
                    resolve(response);
                } else {
                    reject("MEP Dashboard Summary - Error retrieving Emissions by port data!");
                }

            }).catch(() => {
                this.isEmissionsByPortLoading = false;
                reject("MEP Dashboard Summary - Error retrieving Emissions by port data!");
            });
        });
    }

    clearData() {
        this.vesselStatisticsItems = null;
        this.totalEmissions = null;
        this.vesselsByPort = null;
        this.emissionsByPort = null;
        this.vesselsByPortGraph = null;
        this.emissionsByPortGraph = null;
    }

    //Emission type category class
    public getEmissionTypeCategoryClass(emissionTypeCategoryId: number) {
        if (emissionTypeCategoryId == EmissionTypeCategoryEnum.GhgEmissions) {
            return "total-ghg-emissions";
        } else {
            return "total-criteria-pollutants";
        }
    }

    //Emission type category title
    public getEmissionTypeCategoryTitle(emissionTypeCategoryId: number) {
        if (emissionTypeCategoryId == EmissionTypeCategoryEnum.GhgEmissions) {
            return "Total GHG Emissions";
        } else {
            return "Total Criteria Pollutants";
        }
    }

    public getEmissionTypeCategoryTotal(emissionTypeCategoryId: number) {
        if (this.totalEmissions) {
            const emissionsSet = Enumerable.from(this.totalEmissions.totalEmissionsByTypes)
                .where(x => x.emissionTypeCategoryId == emissionTypeCategoryId);

            if (!(this.totalEmissions && this.totalEmissions.totalEmissionsByTypes)) {
                return 0;
            }

            if (emissionTypeCategoryId == EmissionTypeCategoryEnum.GhgEmissions) {
                return emissionsSet.sum(x => x.totalEmissionsQtyTonnes * x.weightingFactor);
            } else {
                return emissionsSet.sum(x => x.totalEmissionsQtyTonnes);
            }
        }
    }

    public getEmissionTypeCategoryUnit(emissionTypeCategoryId: number) {
        if (emissionTypeCategoryId == EmissionTypeCategoryEnum.GhgEmissions) {
            return "tCO<sub>2</sub>e_100yr";
        } else {
            return "tonnes";
        }
    }

    public getTotalEmissionsByScopePerType(emissionTypeCategoryId: number) {
        return this.totalEmissions && this.totalEmissions.totalEmissionsByScopes
            ? this.totalEmissions.totalEmissionsByScopes.filter(x => x.emissionTypeCategoryId == emissionTypeCategoryId && this.filterViewModel.selectedScopes.map(s => s.id).includes(x.scopeId))
            : [];
    }

    public getTotalEmissionsPerType(emissionTypeCategoryId: number) {
        return this.totalEmissions && this.totalEmissions.totalEmissionsByTypes
            ? this.totalEmissions.totalEmissionsByTypes.filter(x => x.emissionTypeCategoryId == emissionTypeCategoryId)
            : [];
    }

    public applyEmissionFormulaFilter(emissionType) {
        if (emissionType) {
            this.filterViewModel.changeEmissionTypesFilter([emissionType.emissionsTypeId]);
            this.filterViewModel.applyAllFilters();
        }
    }

    public setIsVesselCalls(value: boolean) {
        this.isVesselCalls = value;

        //Regenerate the graph when the value changes
        this.vesselsByPortGraph = this.generateVesselsByPortGraph(this.vesselsByPort, this.isVesselCalls ? "vesselCalls" : "tugJobs", 5, "portName");
    }

    public setIsGhgEmissions(value: boolean) {
        this.isGhgEmissions = value;

        //Regenerate the graph when the value changes
        this.emissionsByPortGraph = this.generateEmissionsByPortGraph(this.emissionsByPort, 'totalWeightedEmissionsinTonnes', 5, 'portName');
    }

    //Generate the graph for the vessels by port
    generateVesselsByPortGraph(list, valueParam, columns, yLabelParam) {
        if (!list || list.length == 0) {
            return null;
        }

        const values = list.map(x => x[valueParam]);

        //Get the max value and round it up
        const max = Math.max(...values);
        const maxRoundedUp = this.valueRoundedUp(max);

        const chart = {
            gridXValues: [],
            bars: []
        };

        chart.gridXValues = this.xLabelValues(columns, maxRoundedUp);

        list.forEach(x => {
            chart.bars.push({
                value: x[valueParam],
                valuePercentage: maxRoundedUp == 0 ? 0 : (x[valueParam] / maxRoundedUp) * 100,
                yLabel: x[yLabelParam],
                color: this.getPortColor(x.portId),
            })
        });

        return chart;
    }

    //Generate the graph for the Emissions by port
    generateEmissionsByPortGraph(list, valueParam, columns, yLabelParam) {
        if (!list || list.length == 0) {
            return null;
        }

        //Split into two lists based on the emission type category
        const ghgEmissions = list.filter(x => x.emissionTypeCategoryId == EmissionTypeCategoryEnum.GhgEmissions);
        const criteriaPollutants = list.filter(x => x.emissionTypeCategoryId == EmissionTypeCategoryEnum.CriteriaPollutants);

        let max = 0;

        //Get the max value and round it up
        if (this.isGhgEmissions) {
            max = Math.max(...ghgEmissions.map(x => x.totalWeightedEmissionsinTonnes));
        } else {
            max = Math.max(...criteriaPollutants.map(x => x.totalWeightedEmissionsinTonnes));
        }

        const maxRoundedUp = this.valueRoundedUp(max);

        const chart = {
            gridXValues: [],
            bars: []
        };
        chart.gridXValues = this.xLabelValues(columns, maxRoundedUp);

        const filteredList = this.isGhgEmissions ? ghgEmissions : criteriaPollutants;

        filteredList.forEach(x => {
            chart.bars.push({
                value: Math.ceil(x[valueParam]),
                valuePercentage: (Math.ceil(x[valueParam]) / maxRoundedUp) * 100,
                yLabel: x[yLabelParam],
                color: this.getPortColor(x.portId)
            })
        });

        return chart;
    }

    xLabelValues(columns, maxRoundedUp) {
        const step = maxRoundedUp / columns;

        //Push the values to the result array
        const result = [];
        for (let i = 0; i < columns; i++) {
            const val = Math.ceil(i * step * 10) / 10;
            if (result.indexOf(val) < 0) {
                result.push(val);
            }
        }
        return result;
    }

    valueRoundedUp(num) {

        //Round up based on the number of digits
        const numLength = Math.ceil(num).toString().length;
        const roundUpTo = Math.pow(10, numLength - 1);

        const value = Math.ceil(num / roundUpTo) * roundUpTo;
        return value;
    }

    setBarAlignment(width) {

        if(width > 85){
            return 'align-right';
        }
    }

    assignColorToPort(portIds: number[]) {

        //Create a new array of ojbects with the port ids and the colors 
        if(portIds && portIds.length > 0){
            portIds.forEach((portId, index) => {
                this.portColors.push({portId: portId, color: MepChartColors[index]});
            });        
        }
    }

    getPortColor(portId: number) {

        //Get the color based on the port id
        const portFound = this.portColors.find(x => x.portId == portId);
        return portFound.color;
    }
}